import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Autocomplete, Box, Chip, Stack, TextField, Typography,
} from '@mui/material';
import { TrainingsIcon } from '../global/CustomIcons';
import { format } from '../../utils/datetime';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import ListingToolbar from '../common/ListingToolbar';
import TrainingsTable from '../models/TrainingsTable';
import { fetchRequest as fetchModels } from '../../redux/actions/models/list';
import { fetchRequest as fetchSegments } from '../../redux/actions/segments/list';

const formatDate = (v) => {
  if (!v) return '—';
  try { return format(new Date(v), 'MMM d, yyyy HH:mm'); }
  catch (_e) {
    try { return new Date(v).toLocaleString(); }
    catch (__e) { return String(v); }
  }
};

const STATUS_OPTIONS = [
  { value: 'completed', label: 'Completed', color: 'success' },
  { value: 'failed',    label: 'Failed',    color: 'error'   },
  { value: 'queued',    label: 'Queued',    color: 'warning' },
  { value: 'started',   label: 'Started',   color: 'info'    },
  { value: 'skipped',   label: 'Skipped',   color: 'warning' },
];

const TrainingsPage = () => {
  const dispatch = useDispatch();

  const pipelines = useSelector((s) => s.models_list?.response?.pipelines) || [];
  const segments  = useSelector((s) => s.segments_list?.response?.segments) || [];

  // GET /pipelines is zero-indexed.
  useEffect(() => {
    dispatch(fetchModels({ page: 0, rowsPerPage: 1000 }));
    dispatch(fetchSegments());
  }, [dispatch]);

  const [modelIds, setModelIds] = useState([]);
  const [segmentIds, setSegmentIds] = useState([]);
  const [statuses, setStatuses] = useState([]);

  const filters = useMemo(
    () => ({ modelIds, segmentIds, statuses }),
    [modelIds, segmentIds, statuses],
  );

  const activeFilterCount =
    (modelIds.length > 0 ? 1 : 0)
    + (segmentIds.length > 0 ? 1 : 0)
    + (statuses.length > 0 ? 1 : 0);

  const clearAll = () => {
    setModelIds([]);
    setSegmentIds([]);
    setStatuses([]);
  };

  const selectedPipelines = useMemo(
    () => pipelines.filter((p) => modelIds.includes(p.uid)),
    [pipelines, modelIds],
  );
  const selectedSegments = useMemo(
    () => segments.filter((s) => segmentIds.includes(s.uid)),
    [segments, segmentIds],
  );

  const toggleStatus = (v) =>
    setStatuses((prev) => prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]);

  const filtersNode = (
    <>
      <Autocomplete
        multiple
        size="small"
        options={pipelines}
        value={selectedPipelines}
        onChange={(_, val) => setModelIds(val.map((v) => v.uid))}
        getOptionLabel={(o) => o.name || o.uid || ''}
        isOptionEqualToValue={(a, b) => a.uid === b.uid}
        renderInput={(params) => (
          <TextField {...params} label="Pipeline" placeholder={selectedPipelines.length ? '' : 'Any'} />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.uid}
              label={option.name || option.uid}
              size="small"
            />
          ))
        }
        sx={{ flex: 1, minWidth: 200 }}
        limitTags={2}
      />
      <Autocomplete
        multiple
        size="small"
        options={segments}
        value={selectedSegments}
        onChange={(_, val) => setSegmentIds(val.map((v) => v.uid))}
        getOptionLabel={(o) => o.name || o.uid || ''}
        isOptionEqualToValue={(a, b) => a.uid === b.uid}
        renderInput={(params) => (
          <TextField {...params} label="Segment" placeholder={selectedSegments.length ? '' : 'Any'} />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.uid}
              label={option.name || option.uid}
              size="small"
            />
          ))
        }
        sx={{ flex: 1, minWidth: 200 }}
        limitTags={2}
      />
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary" sx={{
          fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', mr: 0.5,
        }}>
          Status
        </Typography>
        {STATUS_OPTIONS.map((opt) => {
          const selected = statuses.includes(opt.value);
          return (
            <Chip
              key={opt.value}
              label={opt.label}
              size="small"
              onClick={() => toggleStatus(opt.value)}
              color={selected ? opt.color : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
          );
        })}
      </Stack>
    </>
  );

  return (
    <Box sx={{ p: 0, width: '100%' }}>
      <PageBreadcrumbs
        items={[{ text: 'Trainings', icon: <TrainingsIcon /> }]}
        mb={0.5}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, ml: 1 }}>
        Every training run across your pipelines, newest first. Click a row for full details.
      </Typography>

      <ListingToolbar
        filters={filtersNode}
        activeCount={activeFilterCount}
        onClearFilters={clearAll}
      />

      <TrainingsTable
        showPipelineColumn
        formatDate={formatDate}
        filters={filters}
        emptyMessage={
          activeFilterCount > 0
            ? 'No trainings match the current filters.'
            : 'No trainings yet. Create a Pipeline to start producing training runs.'
        }
      />
    </Box>
  );
};

export default TrainingsPage;
